<template>
  <div class="page">
    <n-loading-spinner v-show="loading" />

    <section class="section content-container" v-show="!loading">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column">
            <div class="island">
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th class="icon-column">
                      <n-tooltip class="reload-icon" position="bottom" :text="reloadPageMessage()">
                        <ReloadIcon @click="reloadPredictions" />
                      </n-tooltip>
                    </th>
                    <th>Message</th>
                    <th>Intent</th>
                    <th class="has-text-centered">Add/Remove</th>
                  </tr>
                </thead>

                <tr v-for="prediction in predictions" :key="prediction.utterance">
                  <td></td>
                  <td class="table-column--message">{{ prediction.utterance }}</td>
                  <td class="table-column--intent">
                    <div class="field has-addons">
                      <div class="control is-expanded">
                        <div class="select is-fullwidth" :class="{ 'is-disabled': isActionInProgress() }">
                          <DropDownMenu drop-down-icon="none" class="intent-dropdown" :go-left="false">
                            <template v-slot:trigger>
                              <button class="button">
                                <span class="selected-intent-label">
                                  {{ getFormattedIntentScore(prediction.selectedIntent!) }}
                                </span>
                              </button>
                            </template>

                            <div class="scrollable-item-list">
                              <a
                                v-for="intentScore in prediction.intentScores"
                                :key="intentScore.id"
                                class="dropdown-item"
                                @click="prediction.selectedIntent = intentScore"
                              >
                                {{ getFormattedIntentScore(intentScore) }}
                              </a>
                            </div>
                            <hr class="dropdown-divider" v-if="prediction.intentScores.length > 0" />
                            <a class="dropdown-item" @click="addNewIntent(prediction.utterance)">
                              <div class="add-item">
                                <a class="add-intent">
                                  <AddIcon class="icon-1-3x"></AddIcon>
                                  Add new intent
                                </a>
                              </div>
                            </a>
                          </DropDownMenu>
                        </div>
                      </div>
                      <div class="control edit-icon" :disabled="isActionInProgress()">
                        <a>
                          <EditIcon
                            class="icon-1-2x"
                            @click.stop="editIntent(prediction?.selectedIntent?.id || '')"
                          ></EditIcon>
                        </a>
                      </div>
                    </div>
                  </td>
                  <td class="table-column--actions has-text-centered">
                    <button
                      class="button is-success is-outlined"
                      :class="{
                        'is-loading': isUtteranceBeingAdded(prediction.utterance),
                        'is-disabled': isActionInProgress(),
                      }"
                      @click="addToIntent(prediction.utterance, prediction?.selectedIntent?.id || '')"
                    >
                      <Check class="icon-1-3x"></Check>
                    </button>
                    <button
                      class="button is-danger is-outlined"
                      :class="{
                        'is-loading': isPredictionBeingDeleted(prediction.utterance),
                        'is-disabled': isActionInProgress(),
                      }"
                      @click="deletePrediction(prediction.id)"
                    >
                      <Close class="icon-1-3x"></Close>
                    </button>
                  </td>
                </tr>
              </table>

              <PagingControl
                :total="total"
                :rows-per-page="rowsPerPage"
                @page-changed="onPageChanged"
                :disable-controls="isActionInProgress()"
              ></PagingControl>
            </div>
          </div>
        </div>
      </div>
    </section>

    <AddIntentModal
      ref="addIntentModal"
      v-if="addModalVisible"
      @completed="addedIntent"
      @closed="addModalVisible = false"
    />
    <EditIntentModal
      v-if="showEditModal"
      :intent-id="intentToEditId"
      @closed="showEditModal = false"
      @completed="addedIntent"
      @failed-to-load="showEditModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import PagingControl, { Page } from '@/shared/components/PagingControl.vue';
import Toast from '@/shared/services/toast';
import Check from 'vue-material-design-icons/Check.vue';
import Close from 'vue-material-design-icons/Close.vue';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import ReloadIcon from 'vue-material-design-icons/Reload.vue';
import EditIntentModal from '@/features/bots/components/designer/EditIntentModal.vue';
import AddIntentModal from '@/features/bots/components/designer/AddIntentModal.vue';
import { nlpApi } from '@/features/bots/services/nlp';
import { PredictionViewModel } from '@/features/bots/models/intents/predictionViewModel';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import { IntentScoreViewModel } from '@/features/bots/models/intents/intentScoreViewModel';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { useStore } from 'vuex';
import { computed, nextTick, onMounted, ref } from 'vue';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const predictions = ref<PredictionViewModel[]>([]);
const addingUtterance = ref('');
const deletingPredictionId = ref('');
const loading = ref(true);
const total = ref(0);
const rowsPerPage = ref(25);
const startIndex = ref(0);
const intentToEditId = ref('');
const addModalVisible = ref(false);
const showEditModal = ref(false);

const addIntentModal = ref<typeof AddIntentModal | null>(null);

const emit = defineEmits<{
  (e: 'modal-opened'): void;
}>();

onMounted(() => loadPredictionHistory());

function getFormattedIntentScore(intentScore: IntentScoreViewModel) {
  return intentScore.score === 0 ? `${intentScore.title} (N/A)` : `${intentScore.title} (${intentScore.score})`;
}

function onPageChanged(page: Page) {
  startIndex.value = page.start;
  loadPredictionHistory();
}

function editIntent(id: string) {
  if (isActionInProgress()) {
    return;
  }

  intentToEditId.value = id;
  nextTick(() => {
    showEditModal.value = true;
  });
}

async function addToIntent(utterance: string, intentId: string) {
  if (isActionInProgress()) {
    return;
  }

  try {
    addingUtterance.value = utterance;
    await nlpApi.addUtteranceToIntent(currentWorkspace.value.id, intentId, utterance);
    total.value--;
    predictions.value = predictions.value.filter((p: PredictionViewModel) => p.utterance !== utterance);
    Toast.showSuccess(`Added message to intent`);
  } catch (ex: any) {
    Toast.showError(`Failed to add message to intent: ${ex.message}`);
  } finally {
    addingUtterance.value = '';
  }
}

function isActionInProgress() {
  return addingUtterance.value !== '' || deletingPredictionId.value !== '';
}

function isUtteranceBeingAdded(utterance: string) {
  return addingUtterance.value === utterance;
}

function isPredictionBeingDeleted(utterance: string) {
  return deletingPredictionId.value === utterance;
}

async function reloadPredictions() {
  await loadPredictions();
  Toast.showSuccess(`Latest predictions loaded`);
}

function reloadPageMessage() {
  return 'Refresh page';
}

async function deletePrediction(predictionId: string) {
  if (isActionInProgress()) {
    return;
  }
  try {
    deletingPredictionId.value = predictionId;
    await nlpApi.deletePrediction(currentWorkspace.value.id, predictionId);
    total.value--;
    predictions.value = predictions.value.filter((p: PredictionViewModel) => p.id !== predictionId);
    Toast.showSuccess('Successfully removed message');
  } catch (err: any) {
    Toast.showError(`Failed to remove message: ${err.message}`);
  } finally {
    deletingPredictionId.value = '';
  }
}

async function addNewIntent() {
  addModalVisible.value = true;
  await nextTick(() => {
    if (addIntentModal.value) {
      addIntentModal.value.addNewIntent();
    }
  });
  emit('modal-opened');
}

function addedIntent(intent: IntentViewModel) {
  addModalVisible.value = false;
  showEditModal.value = false;
  const utterances = intent.utterances.map((u) => u.text.toLowerCase());
  const noPredictionsBefore = predictions.value.length;
  predictions.value = predictions.value.filter(
    (p: PredictionViewModel) => !utterances.includes(p.utterance.toLowerCase()),
  );
  total.value -= noPredictionsBefore - predictions.value.length;
}

async function loadPredictionHistory() {
  loading.value = true;
  await loadPredictions();
  loading.value = false;
}

async function loadPredictions() {
  try {
    const pagedResults = await nlpApi.getPredictions(currentWorkspace.value.id, rowsPerPage.value, startIndex.value);
    total.value = pagedResults.total;
    predictions.value = pagedResults.page;
    predictions.value.forEach((prediction: PredictionViewModel) => {
      prediction.selectedIntent = prediction.intentScores[0];
    });
  } catch (err: any) {
    Toast.showError('Failed to load predictions');
  }
}
</script>
<style scoped lang="scss">
.table td {
  vertical-align: middle;
}

.table-column--intent {
  width: 30%;

  .edit-icon {
    margin: auto 0.5em;
    cursor: pointer;

    .material-design-icon {
      vertical-align: top;
    }

    &[disabled] {
      opacity: 0.2;
      cursor: default;
    }
  }
}

.table-column--actions {
  width: 20%;

  button {
    margin-right: 1em;

    &:last-of-type {
      margin-right: 0;
    }

    .material-design-icon__svg {
      bottom: 0;
    }

    &.is-outlined {
      border-color: #dbdbdb;
    }
  }
}

.intent-dropdown {
  width: 100%;
  .button {
    width: 100%;
    justify-content: flex-start;
    border-bottom-right-radius: 10px;
  }

  .selected-intent-label {
    width: 90%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }
}

.field.has-addons {
  .control {
    .button {
      border-bottom-right-radius: 10px;
    }
  }
}

.table-column--intent {
  .field.has-addons {
    .control:first-child:not(:only-child) {
      .button {
        border-radius: 5px;
      }
    }
  }
}

:deep(.dropdown-trigger) {
  width: 100%;
}

:deep(.dropdown-menu) {
  width: 100%;
}

.scrollable-item-list {
  max-height: 400px;
  overflow-y: auto;
}

.icon-column {
  width: 1em;
}

.reload-icon {
  cursor: pointer;
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

.add-item {
  display: flex;

  .icon-1-3x {
    position: relative;
    top: 2px;
    color: $color-netcall-deep-sea;
  }

  .add-intent {
    color: $color-netcall-deep-sea;
  }
}
</style>
